import React, { useMemo } from 'react'
import CarouselCardSmall from 'components/OfferList/OfferCards/OfferCardSmall/CarouselCardSmall'
import CruisePriceDetails from './CruisePriceDetails'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import { getLocationString } from 'checkout/lib/utils/accommodation/location'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'
import { connect } from 'react-redux'
import useIsHomepage from 'home/hooks/isHomepage'
import CruiseDescription from './CruiseDescription'
import CruisesUrgencyTags from 'components/Cruises/CruisesUrgencyTags'
import { getCruiseOfferPriceVariation } from 'lib/cruises/cruiseUtils'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { isUltraLuxOffer } from 'lib/offer/offerUtils'
import CarouselCardMedium from './OfferCardMedium/CarouselCardMedium'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'

interface Props {
  offer: App.CruiseOffer;
  onClick: () => void;
  isVelocityEnabled: boolean;
  className?: string;
  offerUrl: string;
  size: 'small' | 'medium';
}

function CruiseOfferCard(props: Props) {
  const {
    offer,
    onClick,
    className,
    offerUrl,
    size,
    isVelocityEnabled,
  } = props
  const isHomepage = useIsHomepage()
  const { lowestOverallPriceDetails } = offer.mainDepartureDetails
  const velocityFeatures = useMemo(() => (isVelocityEnabled && !isHomepage) ? { canEarn: true, canBurn: true } : undefined, [isHomepage, isVelocityEnabled])

  const discountPills = useMemo(() => {
    if (lowestOverallPriceDetails.discountPills) {
      return {
        totalPrice: lowestOverallPriceDetails.discountPills.price,
        percentage: lowestOverallPriceDetails.discountPills.discountPercentage,
      }
    }

    return undefined
  }, [lowestOverallPriceDetails.discountPills])

  const priceDetails = useMemo(() => {
    const priceVariation =
      getCruiseOfferPriceVariation(offer, lowestOverallPriceDetails)

    return {
      saleUnit: priceVariation.primaryPrice.type,
      totalPrice: priceVariation.primaryPrice.total,
    }
  }, [offer, lowestOverallPriceDetails])

  const isUltraLux = isUltraLuxOffer(offer)

  return (
    <ProductPaletteProvider palette={isUltraLux ? 'ultralux' : 'default'}>
      {size === 'medium' && <CarouselCardMedium
        className={className}
        bookmarkButton={<BookmarkButton offer={offer} />}
        image={offer.images[0]}
        title={offer.name}
        location={getLocationString(offer)}
        to={offerUrl}
        providerName={offer.cruiseLine.name}
        onClick={onClick}
        urgencyLabels={<LabelGroup>
          {isUltraLux && <ProductTypeLabel productType={offer.productType} />}
          <CruisesUrgencyTags tags={offer.urgencyTags} />
        </LabelGroup>}
        priceDetails={
          <CruisePriceDetails
            offerId={offer.id}
            variant="condensed"
            priceDetails={priceDetails}
            discountPills={discountPills}
          />
        }
        description={<CruiseDescription variant="condensed" offer={offer} />}
      />}
      {size === 'small' && <CarouselCardSmall
        className={className}
        bookmarkButton={<BookmarkButton offer={offer} />}
        image={offer.images[0]}
        location={getLocationString(offer)}
        title={offer.name}
        to={offerUrl}
        productType={offer.productType}
        velocityFeatures={velocityFeatures}
        onClick={onClick}
        urgencyLabels={<CruisesUrgencyTags tags={offer.urgencyTags} />}
        priceDetails={
          <CruisePriceDetails
            offerId={offer.id}
            priceDetails={priceDetails}
            discountPills={discountPills} />
        }
        description={<CruiseDescription offer={offer} />}
      />}
    </ProductPaletteProvider>
  )
}

function mapStateToProps(state: App.State) {
  return {
    isVelocityEnabled: state.geo.partnership?.code === VIRGIN_VELOCITY_PARTNERSHIP_CODE,
  }
}

export default connect(mapStateToProps)(CruiseOfferCard)
